import PropTypes from 'prop-types';
import React from 'react';

// Local Date Component

function LocalDate({ dateString, hideTime }) {
  if (dateString == null || dateString === '') return <span> </span>;
  return <span>{ToLocalDate(dateString, hideTime)}</span>;
}

LocalDate.propTypes = {
  dateString: PropTypes.string,
  hideTime: PropTypes.bool,
};

function parseUTC(dateString) {
  if (!dateString) return null;

  // Detect if timezone already exists (Z or ±hh:mm)
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(dateString);

  return new Date(hasTimezone ? dateString : dateString + 'Z');
}

function ToLocalDate(dateString, hideTime = false) {
  const dateObject = parseUTC(dateString);
  if (!dateObject) return '';

  const options = hideTime
    ? {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    : {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      };

  return dateObject.toLocaleString(undefined, options);
}

const toDateTimeLocal = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
};

function FormattedLocalDate({ dateString, hideTime, shortMonth, opts }) {
  if (!dateString) return <span> </span>;

  // Normalize incoming timestamp: if it has no timezone info, append 'Z' to treat it as UTC.
  const normalizeToUTC = (s) => {
    if (!s) return s;

    // If there are fractional seconds with >3 digits, trim to milliseconds precision (3 digits)
    // e.g. ".8935601" -> ".893"
    const fracMatch = s.match(/\.(\d+)(?:$)/);

    if (fracMatch && fracMatch[1].length > 3) {
      s = s.replace(/\.(\d+)(?:$)/, '.' + fracMatch[1].slice(0, 3));
    }

    return s + 'Z'; // mark as UTC
  };

  const normalized = normalizeToUTC(dateString);
  const utcDate = new Date(normalized);

  // Get browser's actual timezone identifier (e.g. "Asia/Kolkata")
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format local date/time using browser locale
  const options = opts || {
    month: shortMonth ? 'short' : 'long',
    day: shortMonth ? '2-digit' : 'numeric',
    year: 'numeric',
  };

  if (!hideTime) {
    options.hour = 'numeric';
    options.minute = '2-digit';
    options.hour12 = true;
  }

  const localDateTime = new Intl.DateTimeFormat(undefined, options).format(utcDate);

  return (
    <span>
      {localDateTime}
      {/* {!hideTime && <span className="text-muted"> ({timeZone})</span>} */}
    </span>
  );
}

FormattedLocalDate.propTypes = {
  dateString: PropTypes.any.isRequired,
  hideTime: PropTypes.any,
  shortMonth: PropTypes.bool,
  opts: PropTypes.object,
};

/**
 * @description Converts a date to a string in the format YYYY-MM-DD
 * @param {Date} date
 * @returns {string} The date string in the format YYYY-MM-DD
 */
const toDate = (date = new Date(), opt = '') => {
  switch (opt) {
    case 'short':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    default:
      return date.toISOString().split('T')[0];
  }
};

export const getCETLocalISOString = (input = new Date()) => {
  const date =
    input instanceof Date
      ? input
      : typeof input === 'string'
        ? new Date(input)
        : new Date();

  if (isNaN(date.getTime())) {
    console.error('Invalid date passed to getCETLocalISOString:', input);
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', // CET / CEST
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  // IMPORTANT: no "Z", no offset
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
};

export const getPacificLocalISOString = (input = new Date()) => {
  const date =
    input instanceof Date
      ? input
      : typeof input === 'string'
        ? new Date(input)
        : new Date();
  if (isNaN(date.getTime())) {
    console.error('Invalid date passed to getPacificLocalISOString:', input);
    return null;
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  // IMPORTANT: no "Z", no offset
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
};

/**
 * Get the start and end dates of the week for a given date (Pacific time).
 * @param {string} focusWeek
 * @returns {{ firstDay: string, lastDay: string }}
 */

export const getUtcToday = () => {
  const now = new Date();
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(), // ⬅️ calendar day only
      0,
      0,
      0,
      0
    )
  );
};

const toUtcDate = (input = null) => {
  // 1️⃣ No input → current instant (UTC timestamp)
  if (input == null) {
    return new Date();
  }

  // 2️⃣ Already a Date → clone it safely
  if (input instanceof Date) {
    return new Date(input.getTime());
  }

  // 3️⃣ Timestamp (number)
  if (typeof input === 'number') {
    return new Date(input);
  }

  // 4️⃣ String input
  if (typeof input === 'string') {
    // If already ISO with timezone, trust it
    if (input.includes('Z') || input.includes('+') || input.includes('-')) {
      return new Date(input);
    }

    // Force UTC interpretation for date-only strings
    return new Date(`${input}T00:00:00.000Z`);
  }
  // 5️⃣ Fallback (should never happen)
  console.warn('Unsupported date input:', input);
  return new Date();
};

const getMostRecentTuesdayUTC = (date) => {
  const day = date.getUTCDay(); // 0–6
  const diff = (day + 5) % 7; // days since Tuesday
  const tuesday = new Date(date.getTime());
  tuesday.setUTCDate(date.getUTCDate() - diff);
  tuesday.setUTCHours(0, 0, 0, 0);
  return tuesday;
};

export const getSelectedWeekStartEndDates = (focusWeek = null) => {
  const utcDate = toUtcDate(focusWeek);

  // 1️⃣ Get correct Tuesday start
  const weekStartTuesday = getMostRecentTuesdayUTC(utcDate);

  // 2️⃣ Monday end = Tuesday + 6 days
  const weekEndMonday = new Date(weekStartTuesday.getTime());
  weekEndMonday.setUTCDate(weekStartTuesday.getUTCDate() + 6);
  weekEndMonday.setUTCHours(23, 59, 59, 999);

  return {
    firstDay: weekStartTuesday,
    lastDay: weekEndMonday,
  };
};
/**
 * Get the start and end dates of the week for a given date plus the previous week.
 * @param {string} focusWeek
 * @returns {{ current: { firstDay: string, lastDay: string }, last: { firstDay: string, lastDay: string }}}
 */
export const getWeekRange = (focusWeek = null) => {
  // Calculate previous week by going back 7 days from the input date
  // const inputDate = focusWeek ? new Date(focusWeek) : new Date();
  const inputDate = toUtcDate(focusWeek);
  const previousWeekDate = new Date(inputDate.getTime() - 7 * 24 * 60 * 60 * 1000);
  // Calculate previous week by going back 7 days from the input date
  const threeWeeksAgoDate = new Date(inputDate.getTime() - 14 * 24 * 60 * 60 * 1000);

  return {
    current: getSelectedWeekStartEndDates(inputDate),
    last: getSelectedWeekStartEndDates(previousWeekDate),
    pay: getSelectedWeekStartEndDates(threeWeeksAgoDate),
  };
};

export const getMonthRange = (focusDate = null) => {
  const baseDate = focusDate ? new Date(focusDate) : new Date();

  // Get UTC calendar date
  const year = baseDate.getUTCFullYear();
  const month = baseDate.getUTCMonth() + 1;

  // Create UTC calendar strings
  const firstDay = `${year}-${String(month).padStart(2, '0')}-01T00:00:00`;

  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastDay = `${year}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}T23:59:59`;

  return {
    firstDay,
    lastDay,
  };
};

const dateIsToday = (date) => {
  const today = new Date();
  const period = new Date(date);
  return (
    today.getFullYear() === period.getFullYear() &&
    today.getMonth() === period.getMonth() &&
    today.getDate() === period.getDate()
  );
};

dateIsToday.propTypes = {
  date: PropTypes.string.isRequired,
};

export { dateIsToday, FormattedLocalDate, toDate, toDateTimeLocal, ToLocalDate };
export default LocalDate;
